import { Button, Div, Label, Loader } from "@grassrootly/rn-core";
import { ScrollView, Platform } from "react-native";
import moment from "moment";
import { GroupTabItemProps } from "../GroupTabs.types";
import { useGroupMacthes } from "./useGroupMatches";
import { IPlayerGame } from "@grassrootly/api";
import { GamesListItem } from "../../../game/games-list";
import { useRef, useEffect, useCallback, useMemo } from "react";

export default function GroupGames({ group, navigation }: GroupTabItemProps) {
  const { loading, groupMatches, loadMoreMatches, loadingMore, hasMoreMatches } = useGroupMacthes({ group });
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const previousMatchesLength = useRef<number>(0);

  const onMatchesPress = (gameId: number) => {
    navigation?.navigate("game", { id: gameId });
  };

  // Memoize sorted matches to prevent unnecessary re-renders
  const sortedMatches = useMemo(() => {
    if (!groupMatches || !Array.isArray(groupMatches)) return [];
    return groupMatches
      .slice() // Create a copy to avoid mutating original array
      .sort((a, b) => moment(b.game_datetime).diff(moment(a.game_datetime)));
  }, [groupMatches]);

  // Store scroll position before updates
  const scrollPositionRef = useRef<number>(0);

  // Preserve scroll position when new matches are loaded
  useEffect(() => {
    if (groupMatches && Array.isArray(groupMatches)) {
      const currentLength = groupMatches.length;
      const previousLength = previousMatchesLength.current;
      
      // If new matches were added (pagination), restore scroll position
      if (currentLength > previousLength && previousLength > 0) {
        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollViewRef.current && scrollPositionRef.current > 0) {
              scrollViewRef.current.scrollTo({ 
                y: scrollPositionRef.current, 
                animated: false 
              });
            }
          });
        });
      }
      
      previousMatchesLength.current = currentLength;
    }
  }, [groupMatches]);

  // Track scroll position before loading more
  const handleScroll = useCallback((event: any) => {
    scrollPositionRef.current = event.nativeEvent.contentOffset.y;
  }, []);


  // Set up intersection observer for auto-loading
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMoreMatches || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingMore && hasMoreMatches) {
          // The scroll position is already being tracked in handleScroll
          loadMoreMatches();
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '50px', // Reduced margin to be less aggressive
      }
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreMatches, loadingMore, loadMoreMatches]);

  return (
    <Div classNames={["flex1"]}>
      {loading && !groupMatches && (
        <Div classNames={["mt-{30}"]}>
          <Loader size="large" isLoading />
        </Div>
      )}

      <ScrollView
        ref={scrollViewRef}
        style={{ 
          marginTop: -16, 
          flex: 1,
          opacity: loading && !groupMatches ? 0 : 1 // Hide instead of unmounting
        }}
        contentContainerStyle={{
          paddingTop: 24,
          paddingBottom: Platform.OS !== "web" ? 100 : 0, // Fixed padding instead of dynamic
        }}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >

          {!loading && groupMatches && (!Array.isArray(groupMatches) || groupMatches.length === 0) && (
            <Label type="l1Bold" classNames={["textCenter", "colorSecondaryLight", "mt-{30}"]}>
              Group Matches not found
            </Label>
          )}

          {sortedMatches.length > 0 && (
            <>
              {sortedMatches.map((game: any, index: number) => (
                <GamesListItem 
                  key={`${game.game_id}-${index}`} 
                  game={game} 
                  showGroupName={false} 
                  onGamePress={onMatchesPress} 
                />
              ))}
              
              {/* Auto-load trigger element */}
              {hasMoreMatches && (
                <div 
                  ref={loadMoreTriggerRef}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    marginTop: '16px', 
                    marginBottom: '16px',
                    minHeight: '20px'
                  }}
                >
                  {loadingMore && <Loader size={24} />}
                </div>
              )}
            </>
          )}
        </ScrollView>
    </Div>
  );
}
